/**
 * Push-to-talk: hold a bare modifier to dictate, release to stop.
 *
 * The trigger is a *modifier* rather than a character key on purpose. The
 * gesture has to coexist with typing in the very textarea it dictates into, and
 * no hold threshold can separate "holding Space to talk" from "typing a space"
 * — either the held key spams characters via auto-repeat, or every keystroke
 * pays the threshold in latency. A bare modifier inserts nothing, so there is
 * nothing to disambiguate.
 *
 * The hold threshold keeps an incidental tap of the modifier from opening the
 * microphone, and any other key joining the hold cancels the gesture so real
 * chords (Ctrl+C, Ctrl+A) behave normally — stopping dictation if it had
 * already started.
 */
import { useEffect, useRef } from "react";

/**
 * Why the microphone is being closed. "release" is the user finishing a
 * deliberate hold; "cancelled" is the gesture being stood down by something
 * else — a chord, a pointer press, focus leaving, an app switch, unmount.
 */
export type PushToTalkStopReason = "release" | "cancelled";

/** How long the modifier must be held before the microphone opens. */
export const PUSH_TO_TALK_HOLD_MS = 400;

/**
 * Control on both platforms: it is the one modifier that is neither the
 * primary chord key on macOS (Command) nor claimed by the OS window manager
 * when tapped alone (Alt focuses the menu bar on Windows).
 */
export const PUSH_TO_TALK_KEY = "Control";

interface UsePushToTalkOptions {
  /** Gate the whole gesture — feature flag, support, composer availability. */
  enabled: boolean;
  /**
   * Whether the element that should receive the transcript holds focus.
   * Checked at keydown: the gesture is scoped to a focused composer so holding
   * Control while reading the conversation never opens the microphone.
   */
  isTargetFocused: () => boolean;
  /**
   * Called once the hold crosses the threshold, and only if `enabled` and
   * `isTargetFocused()` still hold at that moment.
   */
  onStart: () => void;
  /**
   * Called on release, on a cancelling keypress, on a pointer press, when the
   * target loses focus, and on window blur — but only when this hook actually
   * opened the microphone. The reason distinguishes a deliberate finish from a
   * stand-down, which read very differently to a user who never meant to
   * dictate (Control+click is a context menu on macOS, not a hold).
   */
  onStop: (reason: PushToTalkStopReason) => void;
  /**
   * Called at the *start* of the hold, before the threshold. Warming the
   * connection here spends the hold duration on setup the user would otherwise
   * wait through after releasing.
   */
  onHoldStart?: () => void;
}

export function usePushToTalk({
  enabled,
  isTargetFocused,
  onStart,
  onStop,
  onHoldStart,
}: UsePushToTalkOptions): void {
  // Handlers are subscribed once and read current values through refs, so a
  // re-render never drops a keyup between an unsubscribe and a resubscribe —
  // which would strand the microphone open.
  const optsRef = useRef({
    enabled,
    isTargetFocused,
    onStart,
    onStop,
    onHoldStart,
  });
  optsRef.current = { enabled, isTargetFocused, onStart, onStop, onHoldStart };

  useEffect(() => {
    let holdTimer: ReturnType<typeof setTimeout> | null = null;
    // Only true once we actually opened the microphone, so we never call
    // onStop for a session this hook did not start (e.g. a click-started one).
    let dictating = false;

    const clearHoldTimer = () => {
      if (holdTimer !== null) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
    };

    /** Abandon the gesture, closing the microphone if it had opened. */
    const cancel = (reason: PushToTalkStopReason = "cancelled") => {
      clearHoldTimer();
      if (dictating) {
        dictating = false;
        optsRef.current.onStop(reason);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== PUSH_TO_TALK_KEY) {
        // Any other key joining the hold means this was a chord, not a
        // push-to-talk. Let it through untouched and stand down. Checked
        // BEFORE the repeat guard: a character key already auto-repeating when
        // Control goes down still has to cancel, or the mic opens while that
        // key is physically held.
        cancel();
        return;
      }

      // Holding a key repeats keydown; only the first press arms the gesture.
      if (event.repeat) return;

      // Already armed or dictating — nothing to do.
      if (holdTimer !== null || dictating) return;

      const { enabled: on, isTargetFocused: focused } = optsRef.current;
      if (!on || !focused()) return;

      // A modifier already down makes this the tail of a chord (e.g. Shift
      // then Control), not a bare hold.
      if (event.shiftKey || event.altKey || event.metaKey) return;

      optsRef.current.onHoldStart?.();
      holdTimer = setTimeout(() => {
        holdTimer = null;
        // Re-check rather than trusting the keydown-time result: 400ms is long
        // enough for focus to move (a click into another field, a Tab) or for
        // the composer to be disabled. startDictation() targets whatever holds
        // focus *now*, so a stale check lands the transcript in the wrong
        // input — an Assessment textarea on the same page, say.
        const { enabled: stillOn, isTargetFocused: stillFocused } =
          optsRef.current;
        if (!stillOn || !stillFocused()) return;
        dictating = true;
        optsRef.current.onStart();
      }, PUSH_TO_TALK_HOLD_MS);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key !== PUSH_TO_TALK_KEY) return;
      cancel("release");
    };

    // A keyup delivered to another window never reaches us — without this the
    // microphone would stay open after an app switch.
    const handleBlur = () => cancel();

    // On macOS, Control+click IS the secondary click. Without this, holding
    // Control to open a context menu in the composer starts a real dictation,
    // and the native menu can swallow the Control keyup so the mic never
    // closes. Any pointer press stands the gesture down.
    const handlePointer = () => cancel();

    // Focus moving to another element in the same document does not fire
    // window blur; without this the mic stays open until Control is released
    // while the transcript targets whatever took focus.
    const handleFocusOut = () => {
      if (!optsRef.current.isTargetFocused()) cancel();
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("keyup", handleKeyUp, { capture: true });
    document.addEventListener("mousedown", handlePointer, { capture: true });
    document.addEventListener("contextmenu", handlePointer, { capture: true });
    document.addEventListener("focusout", handleFocusOut, { capture: true });
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("keyup", handleKeyUp, { capture: true });
      document.removeEventListener("mousedown", handlePointer, {
        capture: true,
      });
      document.removeEventListener("contextmenu", handlePointer, {
        capture: true,
      });
      document.removeEventListener("focusout", handleFocusOut, {
        capture: true,
      });
      window.removeEventListener("blur", handleBlur);
      cancel();
    };
  }, []);
}
